import { AnimatePresence, motion } from "framer-motion";
import { FolderPlus, Heart, ListPlus, Maximize2, Share2, Sliders, Vibrate } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useFullscreen } from "../../hooks/useFullscreen";
import { useRightSidebarStore } from "../../stores/rightSidebarStore";

interface PlayerMenuProps {
  isLiked: boolean;
  isHapticsSupported: boolean;
  isHapticsEnabled: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onLikeToggle: () => void;
  onShare: () => void;
  onAddToPlaylist: () => void;
  onAddToAlbum: () => void;
  trigger: React.ReactNode;
}

export const PlayerMenu: React.FC<PlayerMenuProps> = ({
  isLiked,
  isHapticsSupported,
  isHapticsEnabled,
  isOpen,
  onOpenChange,
  onLikeToggle,
  onShare,
  onAddToPlaylist,
  onAddToAlbum,
  trigger,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { openSidebar } = useRightSidebarStore();

  const menuItems = [
    {
      icon: Share2,
      label: "Share",
      onClick: onShare,
    },
    {
      icon: Vibrate,
      label: "Configure Haptics",
      onClick: () => openSidebar("haptics"),
      show: isHapticsSupported,
    },
    {
      icon: Sliders,
      label: "Equalizer",
      onClick: () => openSidebar("equalizer"),
    },
    {
      icon: ListPlus,
      label: "Add to Playlist",
      onClick: onAddToPlaylist,
    },
    {
      icon: FolderPlus,
      label: "Add to Album",
      onClick: onAddToAlbum,
    },
    {
      icon: Heart,
      label: isLiked ? "Remove from Liked" : "Add to Liked",
      onClick: onLikeToggle,
    },
    {
      icon: Maximize2,
      label: isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen",
      onClick: toggleFullscreen,
    },
  ].filter((item) => !item.hasOwnProperty("show") || item.show);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (menuRect.right > viewportWidth) {
        menuRef.current.style.right = "0";
        menuRef.current.style.left = "auto";
      }

      if (menuRect.bottom > viewportHeight) {
        menuRef.current.style.bottom = "0";
        menuRef.current.style.top = "auto";
      }
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <div onClick={() => onOpenChange(!isOpen)}>{trigger}</div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 mt-2 w-56 bg-zinc-800 rounded-lg shadow-lg overflow-hidden z-50"
          >
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    item.onClick();
                    onOpenChange(false);
                  }}
                  className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-zinc-700 transition"
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
